import React, { useRef, ReactNode } from "react";
import { Button } from "primereact/button";
import { Stepper } from "primereact/stepper";
import { StepperPanel } from "primereact/stepperpanel";
import { FormLayout } from "../FormLayout/FormLayout";
import { useAtlasTheme } from "../../helpers/themeHelpers";
import styles from "./FormStepper.module.scss";

export interface StepConfig {
  header: string;
  content: ReactNode;
  title: string;
}

export interface FormStepperProps {
  steps: StepConfig[];
  currentStep: number;
  setCurrentStep: (step: number) => void;
  handleSubmit: () => void;
  isNew?: boolean;
  hasReadOnly?: boolean;
  isSubmitting?: boolean;
  submitButtonText?: string;
  saveButtonText?: string;
  loadingButtonText?: string;
  submitDisabled?: boolean;
  afterStepperSlot?: ReactNode;
}

export const FormStepper: React.FC<FormStepperProps> = ({
  steps,
  currentStep,
  setCurrentStep,
  handleSubmit,
  isNew = false,
  hasReadOnly = false,
  isSubmitting = false,
  submitButtonText = "Publish",
  saveButtonText = "Save Assessment",
  loadingButtonText = "Saving...",
  submitDisabled = false,
  afterStepperSlot,
}) => {
  const stepperRef = useRef<any>(null);
  const isAtlas = useAtlasTheme();

  const handleStepChange = (event: { index: number }) => {
    setCurrentStep(event.index);
  };

  // Determine which buttons to show based on current step
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  // Handle navigation
  const handleNext = () => stepperRef.current?.nextCallback();
  const handlePrev = () => stepperRef.current?.prevCallback();

  return (
    <div className={isAtlas ? "relative flex-1 flex flex-col mt-5" : styles.stepperContainer}>
      <Stepper
        activeStep={currentStep}
        ref={stepperRef}
        className={isAtlas ? "mb-2.5" : styles.stepper}
        onChangeStep={handleStepChange}
      >
        {steps.map((step, index) => (
          <StepperPanel key={index} header={step.header} />
        ))}
      </Stepper>

      {afterStepperSlot}

      <div className={isAtlas ? "flex-1" : styles.stepperContent}>
        {isAtlas ? (
          // Atlas: Simple div with 20px padding, no shadow/border
          <div className="w-full p-5">{steps[currentStep]?.content}</div>
        ) : (
          // Legacy: keep FormLayout with Card and title
          <FormLayout title={steps[currentStep]?.title || ""}>
            {steps[currentStep]?.content}
          </FormLayout>
        )}
      </div>

      <div className={isAtlas ? "flex justify-between pt-4 mt-auto" : styles.buttonContainer}>
        {!isFirstStep && (
          <Button
            className={!isAtlas ? styles.primaryButton : "w-40"}
            label="Back"
            icon="pi pi-arrow-left"
            onClick={handlePrev}
            raised={!isAtlas}
          />
        )}
        {isFirstStep && <div />} {/* Empty div for spacing when there is no back button */}
        <div>
          {!isLastStep ? (
            <Button
              className={!isAtlas ? styles.primaryButton : "w-40"}
              label="Next"
              icon="pi pi-arrow-right"
              iconPos="right"
              onClick={handleNext}
              raised={!isAtlas}
            />
          ) : isNew ? (
            <Button
              className={!isAtlas ? styles.primaryButton : "min-w-40"}
              disabled={isSubmitting || hasReadOnly || submitDisabled}
              onClick={handleSubmit}
              icon="pi pi-save"
              iconPos="right"
              raised={!isAtlas}
              label={isSubmitting ? loadingButtonText : submitButtonText}
            />
          ) : (
            <Button
              className={!isAtlas ? styles.outlinedButton : "min-w-40"}
              disabled={isSubmitting || hasReadOnly || submitDisabled}
              onClick={handleSubmit}
              icon="pi pi-save"
              iconPos="right"
              raised={!isAtlas}
              label={isSubmitting ? loadingButtonText : saveButtonText}
            />
          )}
        </div>
      </div>
    </div>
  );
};
